import { Injectable, inject, effect } from '@angular/core';
import { Observable, map, switchMap, of, BehaviorSubject, combineLatest } from 'rxjs';
import { SubscriptionOverviewService } from 'projects/dora/src/app/features/home/data-access/subscription-overview.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { SubIdSelectorService } from '../../sub-id-selector/data-access/sub-id-selector.service';
import { EntitlmentResponse } from '@shared-interfaces/shared-entitlements';
import { AuthService } from 'projects/shared/src/lib/auth/auth/data-access/auth.service';

interface SubscriptionData {
    subscriptionId: string;
}

interface User {
    email: string;
}

@Injectable({
    providedIn: 'root',
})
export class RoleService {
    private subscriptionOverviewService = inject(SubscriptionOverviewService);
    private subIdSelectorService: SubIdSelectorService = inject(SubIdSelectorService);
    private authService = inject(AuthService);
    private entitlementsSubject = new BehaviorSubject<EntitlmentResponse[] | undefined>(undefined);

    constructor() {
        effect(() => {
            const entitlements = this.subIdSelectorService.entitlements();
            this.entitlementsSubject.next(entitlements);
        });
    }

    private adminRole$: Observable<boolean> = combineLatest([
        toObservable(this.subscriptionOverviewService.subscriptionData) as Observable<SubscriptionData>,
        toObservable(this.authService.user) as Observable<User>
    ]).pipe(
        switchMap(([response, user]) => {
            const subscriptionId = response.subscriptionId;
            const userEmail = user?.email;
            
            if (!subscriptionId || !userEmail) return of([]);
            
            // First get subscriptions, then get entitlements
            this.subIdSelectorService.getSubscriptions$.next();
            this.subIdSelectorService.getEntitlements$.next();
            
            return this.entitlementsSubject;
        }),
        map((entitlements: EntitlmentResponse[] | undefined) => {
            if (!entitlements || entitlements.length === 0) return false;
            
            const userEmail = this.authService.user()?.email;
            if (!userEmail) return false;

            // Find the current subscription
            const currentSubscription = entitlements.find(
                entitlement => entitlement.subscriptionId === this.subscriptionOverviewService.subscriptionData().subscriptionId
            );
            
            if (!currentSubscription) return false;

            // Check if the current user has admin role in this subscription
            return currentSubscription.team.some(
                member => member.email === userEmail && member.roles.includes('admin')
            );
        })
    );

    isAdmin(): Observable<boolean> {
        return this.adminRole$;
    }
} 